import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import {
  canActOn,
  integrationCredentials,
  type IntegrationCredential,
  type IssuedIntegrationCredential,
} from "../api/integrationCredentials";

const SECRET_TTL_MS = 10 * 60 * 1000;

export const ALLOWED_SCOPES = [
  "content.subjects.register",
  "content.subjects.read",
  "content.capabilities.issue",
] as const;

/** The one-time secret exists only in component-local state; no module/query/storage copy exists. */
export class EphemeralSecretGuard {
  private generation = 0;
  private mounted = true;

  activate(): void {
    this.mounted = true;
  }

  begin(): number {
    return ++this.generation;
  }

  isCurrent(generation: number): boolean {
    return this.mounted && generation === this.generation;
  }

  invalidate(): void {
    this.mounted = false;
    this.generation += 1;
  }
}

export interface PendingSecret {
  credentialId: string;
  authorization: string;
}

export function maskSecret(value: string): string {
  return value ? "•".repeat(Math.min(value.length, 24)) : "";
}

export function defaultExpiry(days = 30): string {
  const at = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  return new Date(at.getTime() - at.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function isAmbiguous(error: unknown): boolean {
  // No HTTP answer at all, or a gateway-style answer that proves nothing about the server
  // transaction. Everything else is a definitive server decision.
  if (error instanceof ApiError) return error.status >= 500 || error.status === 0;
  return true;
}

export function stateLabel(state: IntegrationCredential["state"]): string {
  switch (state) {
    case "active": return "Активен";
    case "pending": return "Ожидает активации";
    case "expired": return "Истёк";
    case "revoked": return "Отозван";
  }
}

type Action = "issue" | "rotate" | "replace" | "revoke";

const actionLabel: Record<Action, string> = {
  issue: "выпустить новый ключ",
  rotate: "выполнить ротацию",
  replace: "заменить ключ атомарно",
  revoke: "отозвать ключ",
};

export function IntegrationCredentialsPage() {
  const query = useQuery({ queryKey: ["integration-credentials"], queryFn: integrationCredentials.list });
  const [secret, setSecret] = useState<PendingSecret | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<{ action: Action; credential: IntegrationCredential } | null>(null);
  const [issueProvider, setIssueProvider] = useState("alot");
  const [issueScopes, setIssueScopes] = useState<string[]>(["content.subjects.read"]);
  const [issueExpiry, setIssueExpiry] = useState(defaultExpiry);
  const [editScopes, setEditScopes] = useState<string[]>(["content.subjects.read"]);
  const [editExpiry, setEditExpiry] = useState(defaultExpiry);
  const timer = useRef<number | null>(null);
  const guard = useRef(new EphemeralSecretGuard());

  const forgetSecret = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    setSecret(null);
    setRevealed(false);
    setCopied(false);
  }, []);

  // Closing, navigation, or session teardown invalidates every in-flight completion and drops
  // component-local secret state. A late HTTP response therefore cannot schedule a new timer.
  useEffect(() => {
    // React StrictMode replays effect setup/cleanup in development; each setup re-activates the
    // same guard while every cleanup still invalidates completions from the preceding lifetime.
    guard.current.activate();
    return () => {
      guard.current.invalidate();
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  const isCurrent = (generation: number) => guard.current.isCurrent(generation);

  const adoptSecret = (issued: IssuedIntegrationCredential, generation: number) => {
    if (!isCurrent(generation)) return false;
    setSecret({ credentialId: issued.credentialId, authorization: issued.authorization });
    // The value is first shown masked; an operator must ask to reveal it.
    setRevealed(false);
    setCopied(false);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (!isCurrent(generation)) return;
      setSecret(null);
      setRevealed(false);
      setCopied(false);
    }, SECRET_TTL_MS);
    return true;
  };

  async function run(action: Action, credential?: IntegrationCredential) {
    const generation = guard.current.begin();
    // A second operation must never extend the previous one-time secret beyond its own TTL.
    forgetSecret();
    setBusy(true);
    setError(null);
    setMessage(null);
    setConfirming(null);
    try {
      if (action === "issue") {
        const issued = await integrationCredentials.issue({
          providerCode: issueProvider,
          scopes: issueScopes,
          expiresAt: new Date(issueExpiry).toISOString(),
        });
        if (!adoptSecret(issued, generation)) return;
        setMessage("Ключ выпущен. Секрет показан один раз.");
      } else if (credential && action === "rotate") {
        const issued = await integrationCredentials.rotate(credential.providerCode, credential.credentialId, {
          scopes: editScopes,
          expiresAt: new Date(editExpiry).toISOString(),
        });
        if (!adoptSecret(issued, generation)) return;
        setMessage("Ротация выполнена: старый ключ сокращён, новый действует.");
      } else if (credential && action === "replace") {
        const issued = await integrationCredentials.replace(credential.providerCode, credential.credentialId, {
          scopes: editScopes,
          expiresAt: new Date(editExpiry).toISOString(),
        });
        if (!adoptSecret(issued, generation)) return;
        setMessage("Ключ заменён атомарно: старый отозван в той же транзакции.");
      } else if (credential && action === "revoke") {
        await integrationCredentials.revoke(credential.providerCode, credential.credentialId);
        if (!isCurrent(generation)) return;
        setMessage("Ключ отозван.");
      }
      if (isCurrent(generation)) await query.refetch();
    } catch (failure) {
      if (!isCurrent(generation)) return;
      if (isAmbiguous(failure)) {
        // The request may or may not have committed. Do not claim an outcome; reload metadata and say so.
        await query.refetch();
        setError("Результат неизвестен: сервер не подтвердил операцию. Проверьте список ключей.");
      } else if (failure instanceof ApiError) {
        setError(failure.status === 409 || failure.status === 404 ? "Операция отклонена сервером: состояние ключа изменилось." : "Не удалось выполнить операцию.");
        await query.refetch();
      } else {
        setError("Не удалось выполнить операцию.");
      }
    } finally {
      if (isCurrent(generation)) setBusy(false);
    }
  }

  function request(action: Action, credential?: IntegrationCredential) {
    if (action === "issue") return void run(action);
    if (credential && canActOn(credential.state)) setConfirming({ action, credential });
  }

  const toggle = (list: string[], scope: string) =>
    list.includes(scope) ? list.filter((value) => value !== scope) : [...list, scope];

  if (query.isPending) return <p className="muted">Загружаю ключи…</p>;
  if (query.isError) {
    return (
      <div>
        <p className="error" role="alert">Не удалось загрузить ключи интеграций.</p>
        <button type="button" onClick={() => void query.refetch()}>Повторить</button>
      </div>
    );
  }

  const credentials = query.data?.credentials ?? [];

  return (
    <section className="s2s-credentials">
      <header className="page-header">
        <h2>S2S-ключи интеграций</h2>
        <p className="muted">Секрет показывается один раз и никогда не сохраняется в браузере.</p>
      </header>

      {message && <p className="notice" role="status">{message}</p>}
      {error && <p className="error" role="alert">{error}</p>}

      {secret && (
        <div className="secret-panel" role="group" aria-label="Одноразовый секрет">
          <p>Секрет ключа <code>{secret.credentialId}</code> доступен только сейчас.</p>
          <output className="secret-value" data-testid="secret-value">
            {revealed ? secret.authorization : maskSecret(secret.authorization)}
          </output>
          <div className="actions">
            <button type="button" onClick={() => setRevealed(true)} disabled={revealed}>Показать</button>
            <button
              type="button"
              onClick={() => {
                if (!navigator.clipboard) {
                  setError("Буфер обмена недоступен. Секрет не был скопирован.");
                  return;
                }
                void navigator.clipboard.writeText(secret.authorization)
                  .then(() => setCopied(true))
                  .catch(() => setError("Не удалось скопировать секрет. Разрешите доступ к буферу обмена и повторите."));
              }}
            >
              {copied ? "Скопировано" : "Копировать"}
            </button>
            <button type="button" onClick={forgetSecret}>Закрыть</button>
          </div>
        </div>
      )}

      {confirming && (
        <div className="confirm-panel" role="alertdialog" aria-label="Подтверждение операции">
          <p>Подтвердите: {actionLabel[confirming.action]} для {confirming.credential.providerCode} / {confirming.credential.credentialId}?</p>
          <p className="muted">
            {confirming.action === "revoke"
              ? "Отзыв необратим: ключ сразу перестанет работать."
              : confirming.action === "rotate"
                ? "Старый ключ продолжит работать не более 24 часов, чтобы площадка успела перейти на новый."
                : "Новый ключ будет выпущен, а старый немедленно отозван в одной транзакции."}
          </p>
          <div className="actions">
            <button type="button" disabled={busy} onClick={() => void run(confirming.action, confirming.credential)}>Подтвердить</button>
            <button type="button" onClick={() => setConfirming(null)}>Отмена</button>
          </div>
        </div>
      )}

      <fieldset className="issue-panel" disabled={busy}>
        <legend>Новый ключ</legend>
        <label>
          Провайдер
          <select value={issueProvider} onChange={(event) => setIssueProvider(event.target.value)}>
            <option value="alot">alot</option>
            <option value="wiki">wiki</option>
          </select>
        </label>
        <label>
          Действует до
          <input type="datetime-local" value={issueExpiry} onChange={(event) => setIssueExpiry(event.target.value)} />
        </label>
        <div className="scopes">
          {ALLOWED_SCOPES.map((scope) => (
            <label key={scope}>
              <input type="checkbox" checked={issueScopes.includes(scope)} onChange={() => setIssueScopes(toggle(issueScopes, scope))} />
              {scope}
            </label>
          ))}
        </div>
        <button type="button" disabled={busy || issueScopes.length === 0} onClick={() => request("issue")}>Выпустить</button>
      </fieldset>

      <fieldset className="issue-panel" disabled={busy}>
        <legend>Параметры ротации / замены</legend>
        <label>
          Действует до
          <input type="datetime-local" value={editExpiry} onChange={(event) => setEditExpiry(event.target.value)} />
        </label>
        <div className="scopes">
          {ALLOWED_SCOPES.map((scope) => (
            <label key={scope}>
              <input type="checkbox" checked={editScopes.includes(scope)} onChange={() => setEditScopes(toggle(editScopes, scope))} />
              {scope}
            </label>
          ))}
        </div>
      </fieldset>

      <table className="table">
        <thead>
          <tr>
            <th>Провайдер</th>
            <th>Ключ</th>
            <th>Состояние</th>
            <th>Scopes</th>
            <th>Действует с</th>
            <th>Действует до</th>
            <th>Создан</th>
            <th>Действия</th>
          </tr>
        </thead>
        <tbody>
          {credentials.length === 0 && (
            <tr><td colSpan={8} className="muted">Ключей ещё нет.</td></tr>
          )}
          {credentials.map((credential) => {
            const active = canActOn(credential.state);
            return (
              <tr key={credential.credentialId} data-state={credential.state}>
                <td>{credential.providerCode}</td>
                <td><code>{credential.credentialId}</code></td>
                <td><span className={`state ${credential.state}`}>{stateLabel(credential.state)}</span></td>
                <td>{credential.scopes.join(", ")}</td>
                <td>{new Date(credential.notBefore).toLocaleString()}</td>
                <td>{new Date(credential.expiresAt).toLocaleString()}</td>
                <td>{new Date(credential.createdAt).toLocaleString()}</td>
                <td className="actions">
                  <button type="button" disabled={busy || !active} onClick={() => request("rotate", credential)}>Ротация</button>
                  <button type="button" disabled={busy || !active} onClick={() => request("replace", credential)}>Заменить</button>
                  <button type="button" disabled={busy || !active} onClick={() => request("revoke", credential)}>Отозвать</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
